import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { fetchMyReferral } from "../../api/growthOffers";
import useGrowthOffers, { referralOpen } from "../../hooks/useGrowthOffers";
import { trackReferralShare } from "../../utils/analytics";

/**
 * Invite friends: the shopper's own invite link, with WhatsApp first because
 * that is where Kampala shares. The deal is the server's (a free first
 * delivery for the friend, a free delivery for the inviter once the friend's
 * order is delivered); this page only shows it and the counts.
 */

const inviteMessage = (shareUrl) =>
  `I order on Lusuku: restaurants, shops and pharmacies delivered, and you can pay on delivery. Your first delivery is free with my link: ${shareUrl}`;

const InviteFriends = () => {
  const offers = useGrowthOffers();
  const [referral, setReferral] = useState(null);
  const [status, setStatus] = useState("loading");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    fetchMyReferral()
      .then((data) => {
        if (!active) return;
        setReferral(data);
        setStatus("ready");
      })
      .catch(() => active && setStatus("error"));
    return () => {
      active = false;
    };
  }, []);

  if (offers && !referralOpen(offers)) {
    return (
      <Panel>
        <Title>Invite friends</Title>
        <Body>Invites are not open yet. Check back soon.</Body>
      </Panel>
    );
  }

  if (status === "loading") return <Panel><Body>Loading your invite link</Body></Panel>;
  if (status === "error" || !referral?.shareUrl) {
    return (
      <Panel>
        <Title>Invite friends</Title>
        <Body>Your invite link could not be loaded. Please try again shortly.</Body>
      </Panel>
    );
  }

  const message = inviteMessage(referral.shareUrl);
  const shareOnWhatsApp = () => {
    trackReferralShare("referral", "whatsapp");
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };
  const copyLink = async () => {
    trackReferralShare("referral", "copy");
    try {
      await navigator.clipboard.writeText(referral.shareUrl);
      setCopied(true);
    } catch (_) {
      setCopied(false);
    }
  };
  const shareNatively = async () => {
    trackReferralShare("referral", "native");
    try {
      await navigator.share({ title: "Lusuku", text: message, url: referral.shareUrl });
    } catch (_) {
      /* the shopper closed the share sheet */
    }
  };
  const canShareNatively = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <Panel>
      <Title>Give a friend their first delivery free</Title>
      <Body>
        When their first order is delivered, you get a free delivery too.
      </Body>
      <LinkBox aria-label="Your invite link">{referral.shareUrl}</LinkBox>
      <Actions>
        <Primary type="button" onClick={shareOnWhatsApp}>Share on WhatsApp</Primary>
        <Secondary type="button" onClick={copyLink}>{copied ? "Link copied" : "Copy link"}</Secondary>
        {canShareNatively && (
          <Secondary type="button" onClick={shareNatively}>More ways to share</Secondary>
        )}
      </Actions>
      <Stats>
        <Stat>
          <strong>{Number(referral.invitedCount) || 0}</strong>
          <span>friends joined</span>
        </Stat>
        <Stat>
          <strong>{Number(referral.rewardedCount) || 0}</strong>
          <span>first orders delivered</span>
        </Stat>
        <Stat>
          <strong>{Number(referral.freeDeliveryCredits) || 0}</strong>
          <span>free deliveries to use</span>
        </Stat>
      </Stats>
    </Panel>
  );
};

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  max-width: 640px;
  color: #15232a;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
`;

const Body = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #4b5a60;
`;

const LinkBox = styled.code`
  display: block;
  padding: 12px;
  border-radius: 10px;
  background: #f5f7f2;
  border: 1px solid #e1e6de;
  font-size: 14px;
  word-break: break-all;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Primary = styled.button`
  min-height: 44px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: #3d6a0b;
  color: #ffffff;
  font-weight: 800;
  font-size: 15px;
  cursor: pointer;
`;

const Secondary = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border-radius: 12px;
  border: none;
  background: #e3eef2;
  color: #15232a;
  font-weight: 700;
  font-size: 15px;
  cursor: pointer;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px;
  border-radius: 12px;
  background: #f5f7f2;

  strong {
    font-size: 20px;
  }

  span {
    font-size: 13px;
    color: #4b5a60;
  }
`;

export default InviteFriends;
